import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of } from 'rxjs';
import { SoffApiResponseDto } from '@soff/shared-ui';
import * as IncomingTopicsV2Actions from './actions';
import {
  IncomingTopicsSto,
  IncomingTopicTileDto,
  RelatedIncomingTopicTypeDto,
  RelatedIncomingTopicTypeSto,
  RelatedObjectSto,
} from '../models/incoming-topic.model';
import { IncomingTopicsV2Service } from '../services/incoming-topics-v2.service';

@Injectable()
export class IncomingTopicsV2Effects {
  private actions$ = inject(Actions);
  private incomingTopicsV2Service = inject(IncomingTopicsV2Service);

  changeIncomingTopicStatus = createEffect(() =>
    this.actions$.pipe(
      ofType(IncomingTopicsV2Actions.changeIncomingTopicStatus),
      mergeMap(({ aggregateId, status, comment }) => {
        return this.incomingTopicsV2Service.changeIncomingTopicStatus(aggregateId, { status, comment }).pipe(
          map((_: SoffApiResponseDto) => IncomingTopicsV2Actions.changeIncomingTopicStatusSuccessful({ aggregateId, status, comment })),
          catchError(() => of(IncomingTopicsV2Actions.changeIncomingTopicStatusFailed()))
        );
      })
    )
  );

  loadIncomingTopicDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IncomingTopicsV2Actions.loadIncomingTopicDetails),
      mergeMap(({ aggregateId }) => {
        return this.incomingTopicsV2Service.getIncomingTopicById(aggregateId).pipe(
          map((res: SoffApiResponseDto) =>
            IncomingTopicsV2Actions.loadIncomingTopicDetailsSuccessful({
              topic: new IncomingTopicsSto(Array.isArray(res?.data) ? res?.data?.[0] : res?.data),
              related: (res?.related ?? []) as RelatedObjectSto[],
            })
          ),
          catchError(() => of(IncomingTopicsV2Actions.loadIncomingTopicDetailsFailed()))
        );
      })
    )
  );

  loadIncomingTopicsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IncomingTopicsV2Actions.loadIncomingTopicsPage),
      mergeMap(({ viewId, query }) => {
        return this.incomingTopicsV2Service.getIncomingTopics(query).pipe(
          map((res: SoffApiResponseDto) =>
            IncomingTopicsV2Actions.loadIncomingTopicsPageSuccessful({
              viewId,
              topics: res?.data?.map(dto => new IncomingTopicsSto(dto)),
              topicsTotalCount: res?.meta?.pagination?.total,
              related: res?.related,
              query,
            })
          ),
          catchError(() => of(IncomingTopicsV2Actions.loadIncomingTopicsPageFailed({ viewId })))
        );
      })
    )
  );

  assignResource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IncomingTopicsV2Actions.assignResource),
      mergeMap(({ aggregateId, assignedResourceId }) => {
        return this.incomingTopicsV2Service.patchIncomingTopic(aggregateId, { assignedResourceId }).pipe(
          map((_: SoffApiResponseDto) => IncomingTopicsV2Actions.assignResourceSuccessful({ aggregateId, assignedResourceId })),
          catchError(() => of(IncomingTopicsV2Actions.assignResourceFailed()))
        );
      })
    )
  );

  patchDueAt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IncomingTopicsV2Actions.patchDueAt),
      mergeMap(({ aggregateId, dueAt }) => {
        return this.incomingTopicsV2Service.patchIncomingTopic(aggregateId, { dueAt }).pipe(
          map((_: SoffApiResponseDto) => IncomingTopicsV2Actions.patchDueAtSuccessful({ aggregateId, dueAt })),
          catchError(() => of(IncomingTopicsV2Actions.patchDueAtFailed()))
        );
      })
    )
  );

  loadTiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IncomingTopicsV2Actions.loadTiles),
      mergeMap(() => {
        return this.incomingTopicsV2Service.loadTiles().pipe(
          map((res: SoffApiResponseDto) =>
            IncomingTopicsV2Actions.loadTilesSuccessful({
              tiles: (res?.data as IncomingTopicTileDto[]) ?? [],
              types:
                res?.related
                  ?.filter(r => r.aggregateType === 'IncomingTopicType')
                  .map(r => new RelatedIncomingTopicTypeSto(r as RelatedIncomingTopicTypeDto)) ?? [],
            })
          ),
          catchError(() => of(IncomingTopicsV2Actions.loadTilesFailed()))
        );
      })
    )
  );
}
